import React from "react";
import type { FunctionalCode } from "../../types/residents";
import { FUNCTIONAL_CODE_LABELS, isFunctionalScore } from "../../types/residents";

const SCORE_STYLES: Record<number, { bg: string; text: string }> = {
  6: { bg: "#e6f9ed", text: "#009636" },
  5: { bg: "#d1fae5", text: "#065f46" },
  4: { bg: "#f0f9ff", text: "#0369a1" },
  3: { bg: "#dbeafe", text: "#1e40af" },
  2: { bg: "#fef2f2", text: "#c4292e" },
  1: { bg: "#fecaca", text: "#991b1b" },
};

const NA_STYLE = { bg: "#f1f5f9", text: "#64748b" };

// Short, readable tokens for "Activity Not Attempted" / non-scored MDS codes.
// These outcomes were recorded by a clinician but carry no 1-6 performance
// score, so they must read differently from both a real score and from a
// blank "no data" cell (NETSOL-11680). The numeric code stays in the tooltip.
const NON_SCORE_ABBREV: Record<number, string> = {
  7: "REF", // Refused
  9: "N/A", // Not applicable
  10: "ENV", // Not attempted — environmental limitation
  88: "MED", // Not attempted — medical condition / safety
};

interface FunctionalAbilityScoreCellProps {
  code: FunctionalCode;
  small?: boolean;
}

export const FunctionalAbilityScoreCell: React.FC<
  FunctionalAbilityScoreCellProps
> = ({ code, small = false }) => {
  // No data: the assessment item was never recorded for this shift/day.
  if (code === null) {
    return (
      <span
        className="text-gray-300 inline-block min-w-[28px] text-center"
        title="No data"
      >
        &mdash;
      </span>
    );
  }

  const isScore = isFunctionalScore(code);
  const style = isScore ? SCORE_STYLES[code] : NA_STYLE;
  const label = FUNCTIONAL_CODE_LABELS[code] || String(code);
  // Real scores show the 1-6 number; non-scored outcomes show a labeled
  // token (with a dashed outline) so they are visually distinct from scores.
  const display = isScore ? code : (NON_SCORE_ABBREV[code] ?? String(code));

  return (
    <span
      className={`inline-block rounded text-center font-semibold whitespace-nowrap ${
        isScore ? "" : "border border-dashed border-slate-300 italic"
      } ${
        small
          ? "px-1.5 py-px text-[10px] min-w-5 font-bold"
          : "px-2.5 py-1 text-xs min-w-8"
      }`}
      style={{ backgroundColor: style.bg, color: style.text }}
      title={`${code} - ${label}`}
    >
      {display}
    </span>
  );
};
